import { useState, useEffect } from "react"
import { useParams } from "react-router"
import { FileText, CheckCircle2, XCircle, Clock, ShieldCheck, ChevronRight, UserCheck } from "lucide-react"
import { SchoolLayout } from "../components/ui/school/SchoolLayout"
import { StudentHero } from "../components/ui/school/student-overview/student-hero"
import { AchievementsCard } from "../components/ui/school/student-overview/achievements-card"
import { PerformanceTabs } from "../components/ui/school/student-overview/performance-tabs"
import { CareerPathsCard } from "../components/ui/school/student-overview/career-paths-card"
import {
  getStudent, verifyStudent, verifyStudentResult, rejectStudentResult,
} from "../hooks/api/schoolApi"
import {
  sampleStudent, sampleTasks, sampleActivityLogs,
  nigerianSubjects, enrolledSubjects, careerPathsData, achievementsData,
} from "../lib/student-overview-data"

// ─── Verification Panel ───────────────────────────────────────────────────────
function AcademicResultsPanel({ studentId, results = [], onResultsChange }) {
  const [noteMap, setNoteMap] = useState({})
  const [saving, setSaving] = useState({})

  const handleVerify = async (resultId) => {
    setSaving((s) => ({ ...s, [resultId]: true }))
    await verifyStudentResult(studentId, resultId)
    onResultsChange((prev) =>
      prev.map((r) => r.id === resultId ? { ...r, status: "verified", verifiedAt: new Date().toISOString() } : r)
    )
    setSaving((s) => ({ ...s, [resultId]: false }))
  }

  const handleReject = async (resultId) => {
    const note = noteMap[resultId] ?? ""
    setSaving((s) => ({ ...s, [resultId]: true }))
    await rejectStudentResult(studentId, resultId, note)
    onResultsChange((prev) =>
      prev.map((r) => r.id === resultId ? { ...r, status: "rejected", adminNote: note } : r)
    )
    setSaving((s) => ({ ...s, [resultId]: false }))
  }

  const pending = results.filter((r) => r.status === "pending").length

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
      <div className="flex items-center gap-2">
        <ShieldCheck size={18} className="text-indigo-500" />
        <h3 className="font-bold text-slate-900">Academic Results</h3>
        {pending > 0 && (
          <span className="ml-auto bg-amber-100 text-amber-700 text-xs font-bold px-2 py-0.5 rounded-full">
            {pending} pending
          </span>
        )}
      </div>

      {results.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-4">No uploads yet</p>
      ) : (
        <div className="space-y-3">
          {results.map((r) => {
            const isBusy = saving[r.id]
            const statusIcon = r.status === "verified" ? CheckCircle2 : r.status === "rejected" ? XCircle : Clock
            const statusColor = r.status === "verified" ? "text-emerald-600" : r.status === "rejected" ? "text-red-600" : "text-amber-600"
            const Icon = statusIcon
            return (
              <div key={r.id} className="rounded-xl border border-slate-200 p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <FileText size={16} className="text-slate-400 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800">{r.label}</p>
                    <p className="text-xs text-slate-500">{r.fileName}</p>
                    <p className="text-xs text-slate-400">Uploaded {new Date(r.uploadedAt).toLocaleDateString()}</p>
                  </div>
                  <div className={`flex items-center gap-1 text-xs font-semibold shrink-0 ${statusColor}`}>
                    <Icon size={12} /> {r.status}
                  </div>
                </div>

                {r.status === "pending" && (
                  <div className="space-y-2">
                    <textarea
                      value={noteMap[r.id] ?? ""}
                      onChange={(e) => setNoteMap((m) => ({ ...m, [r.id]: e.target.value }))}
                      placeholder="Optional note for student..."
                      rows={2}
                      className="w-full text-xs rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    />
                    <div className="flex gap-2">
                      <button
                        disabled={isBusy}
                        onClick={() => handleVerify(r.id)}
                        className="flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white text-xs font-bold px-3 py-2 rounded-xl transition-all"
                      >
                        <CheckCircle2 size={12} /> {isBusy ? "…" : "Verify"}
                      </button>
                      <button
                        disabled={isBusy}
                        onClick={() => handleReject(r.id)}
                        className="flex items-center gap-1.5 bg-red-100 hover:bg-red-200 disabled:opacity-50 text-red-700 text-xs font-bold px-3 py-2 rounded-xl transition-all"
                      >
                        <XCircle size={12} /> {isBusy ? "…" : "Reject"}
                      </button>
                    </div>
                  </div>
                )}
                {r.adminNote && r.status !== "pending" && (
                  <p className="text-xs text-slate-500 bg-slate-50 rounded-lg px-3 py-2">Note: {r.adminNote}</p>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ─── Verify Student Banner ────────────────────────────────────────────────────
function VerifyStudentBanner({ student, onVerified }) {
  const [verifying, setVerifying] = useState(false)

  if (!student || student.status === "active") return null

  const handleVerify = async () => {
    setVerifying(true)
    await verifyStudent(student._id ?? student.id)
    onVerified()
    setVerifying(false)
  }

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <UserCheck className="w-5 h-5 text-amber-600 shrink-0" />
        <div>
          <p className="text-sm font-semibold text-amber-900">Account Pending Verification</p>
          <p className="text-xs text-amber-700">This student's account is not yet marked as active.</p>
        </div>
      </div>
      <button
        disabled={verifying}
        onClick={handleVerify}
        className="shrink-0 px-4 py-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white text-sm font-bold rounded-xl transition-all"
      >
        {verifying ? "Verifying…" : "Verify Student"}
      </button>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────
const StudentOverviewPage = () => {
  const { id } = useParams()
  const [student, setStudent] = useState(null)
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(!!id)

  useEffect(() => {
    if (!id) { setLoading(false); return }
    getStudent(id)
      .then((s) => {
        setStudent(s)
        setResults(s.academicResults ?? [])
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [id])

  // Fall back to sample data when no id (legacy /student-overview route)
  const displayName = student ? `${student.firstName} ${student.lastName}` : sampleStudent.name
  const displayId = student?.studentId ?? sampleStudent.studentId
  const displayClass = student?.classLevel ?? sampleStudent.class
  const displayStatus = student?.status ?? sampleStudent.status

  return (
    <SchoolLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="flex text-sm text-gray-500 font-medium mb-6">
          <a className="hover:text-teal-600 transition-colors" href="/students-management">Students</a>
          <ChevronRight className="w-4 h-4 mx-2 mt-0.5" />
          <span className="text-gray-900">{loading ? "Loading…" : displayName}</span>
        </nav>

        {loading ? (
          <div className="flex justify-center py-20 text-gray-400">Loading student…</div>
        ) : (
          <div className="flex flex-col gap-6">
            {/* Verify banner — only when account is not active */}
            {student && (
              <VerifyStudentBanner
                student={student}
                onVerified={() => setStudent((s) => s ? { ...s, status: "active" } : s)}
              />
            )}

            <StudentHero
              name={displayName}
              studentId={displayId}
              class={displayClass}
              house={sampleStudent.house}
              careerPath={sampleStudent.careerPath}
              status={displayStatus}
              enrolledSubjects={enrolledSubjects}
            />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              <div className="lg:col-span-8 flex flex-col gap-6">
                <CareerPathsCard careerPaths={careerPathsData} />
                <PerformanceTabs
                  subjects={nigerianSubjects}
                  tasks={sampleTasks}
                  activities={sampleActivityLogs}
                  tasksCompleted={45}
                  currentStreak={12}
                  engagementRate={87}
                  attendanceRate={95}
                />
              </div>

              <div className="lg:col-span-4 flex flex-col gap-6">
                <AchievementsCard achievements={achievementsData} />
                <AcademicResultsPanel
                  studentId={id ?? student?._id}
                  results={results}
                  onResultsChange={setResults}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </SchoolLayout>
  )
}

export default StudentOverviewPage
